/**
 * A draft, as the person reviewing it before Publish sees it.
 *
 * ## THIS IS THE SCREEN "A HUMAN SHIPS WHAT THEY REVIEWED" DEPENDS ON
 *
 * The API refuses a publish of any version but the one named, and this is what
 * the person was looking at when they named it. So it shows everything a
 * publish sends: the budget **as money, in the account's currency, with its
 * unit**, the end date, who it targets and where, the Page and Instagram
 * account it runs as, and every word, link and file the draft holds for every
 * ad — as facts with labels, and nothing that argues. (A chat ad's address is
 * Meta's constant, not the draft's; its destination says where it leads.) The version check proves which version
 * shipped; only this screen can make that the version somebody read. The
 * version itself travels as `data-version`, not as text: it is the API's
 * guard, and a number a person reads nothing from.
 *
 * The budget is the one figure drawn large, because it is the one whose unit
 * mistake costs money: `formatMetaMoney` divides the stored basic units by
 * Meta's offset at the last moment, so a draft that somehow held rupees as
 * paise would draw as ₹200,050.00 here, where a reviewer can see it, rather
 * than reaching Meta.
 *
 * ## "PUBLISHED PAUSED" IS WHAT WEND DID, NOT WHAT META SHOWS NOW
 *
 * The record is not a reading of Meta: once a campaign is there, somebody can
 * start or pause it in Ads Manager and this record will not know. So the state
 * is worded as the event it records — "Published paused", and since slice 43
 * "Started" — and drawn neutral: slice 41's rule is that only a live delivery
 * earns green, and a green badge here would be a claim about spend nobody
 * measured. What Meta shows now is the draft page's delivery panel.
 *
 * ## A PUBLISH THE API TREATS AS DEAD IS A PUBLISH THAT STOPPED
 *
 * A `publishing` row the API has not touched for `META_PUBLISH_LEASE_MINUTES`
 * is treated as a publish that died, and the API will claim it again. Drawn as
 * "Publishing" it would look exactly like one in flight, for ever, with nothing
 * to press; {@link publishStalled} draws it as stopped and makes it
 * publishable, on the same threshold the API uses — measured on this
 * browser's clock, so one that runs fast offers "Publish again" early, and the
 * API answers that the publish is still running.
 *
 * ## WHY IT IS ITS OWN FILE
 *
 * The review page reaches the API client and so `import.meta.env`, which a
 * verify script cannot load. This imports only contracts, the design system
 * and the formatters, so `verify-meta-ads-composer` renders it directly.
 */
import { Badge, Kicker, Spinner } from '@wend/ui';
import { Pause, Play } from 'lucide-react';
import type { ReactNode } from 'react';

/* `@wend/shared` directly, never through the API client. */
import {
  META_BUDGET_LABELS,
  META_PUBLISH_LEASE_MINUTES,
  metaDraftDestination,
  type MetaAdCreative,
  type MetaAdDestination,
  type MetaAdDraftRecord,
  type MetaAdObjective,
  type MetaCta,
  type MetaDraftStatus,
  type MetaSpecialAdCategory,
} from '@wend/shared';

import { formatDate } from '../../lib/format';
import { formatMetaMoney } from '../integrations/MetaAdsAccountRead';

export const OBJECTIVE_LABEL: Readonly<Record<MetaAdObjective, string>> = {
  OUTCOME_AWARENESS: 'Awareness',
  OUTCOME_TRAFFIC: 'Traffic',
};

export const CATEGORY_LABEL: Readonly<Record<MetaSpecialAdCategory, string>> = {
  NONE: 'No special category',
  FINANCIAL_PRODUCTS_SERVICES: 'Financial products and services',
  HOUSING: 'Housing',
  EMPLOYMENT: 'Employment',
  ISSUES_ELECTIONS_POLITICS: 'Social issues, elections or politics',
};

export const FORMAT_LABEL: Readonly<Record<MetaAdCreative['format'], string>> = {
  single_image: 'Single image',
  carousel: 'Carousel',
  single_video: 'Video',
  /* Slice 47: the same media; where it leads is the draft's destination. */
  chat_image: 'Single image',
  chat_video: 'Video',
};

/** Slice 47. Where a draft's ads lead, in the composer's words. */
export const DESTINATION_LABEL: Readonly<Record<MetaAdDestination, string>> = {
  website: 'Website',
  whatsapp: 'WhatsApp chat',
};

/** A named placement, in the composer's words. */
const PLACEMENT_LABEL: Readonly<Record<'facebook' | 'instagram' | 'whatsapp', string>> = {
  facebook: 'Facebook',
  instagram: 'Instagram',
  whatsapp: 'WhatsApp Status',
};

/** The button an ad shows: the one chosen, or — for an ad that opens a chat — Meta's own. */
function buttonLabel(creative: MetaAdCreative): string {
  switch (creative.format) {
    case 'chat_image':
    case 'chat_video':
      return 'Send WhatsApp message';
    default:
      return CTA_LABEL[creative.cta];
  }
}

/** The button, in the words the composer offered it in. */
export const CTA_LABEL: Readonly<Record<MetaCta, string>> = {
  LEARN_MORE: 'Learn more',
  APPLY_NOW: 'Apply now',
  SIGN_UP: 'Sign up',
  CONTACT_US: 'Contact us',
  GET_QUOTE: 'Get quote',
  SUBSCRIBE: 'Subscribe',
};

type StatusTone = 'neutral' | 'info' | 'good' | 'warn' | 'bad';

/** The status, as a badge. See the header for why `published` is neutral. */
export const STATUS_DISPLAY: Readonly<Record<MetaDraftStatus, { readonly label: string; readonly tone: StatusTone }>> = {
  draft: { label: 'Draft', tone: 'neutral' },
  publishing: { label: 'Publishing', tone: 'info' },
  published: { label: 'Published paused', tone: 'neutral' },
  failed: { label: 'Publish stopped', tone: 'bad' },
};

/**
 * A `publishing` row past the lease — one the API treats as dead and will
 * claim again. `now` is this browser's clock; see the header.
 */
export function publishStalled(record: MetaAdDraftRecord, now: number): boolean {
  return (
    record.status === 'publishing' &&
    now - Date.parse(record.updatedAt) > META_PUBLISH_LEASE_MINUTES * 60_000
  );
}

/**
 * Slice 43: what Wend has done to a published campaign since, worded as the
 * event like "Published paused" and neutral for the same reason — what Wend
 * did, not a live reading; the draft page's delivery panel is that. "Paused"
 * is not "Published paused": one ran and was stopped, the other never ran.
 * "Not confirmed" is the one that is not neutral: Meta never said whether
 * Wend's last start or pause took, so the campaign may be running.
 */
export const DELIVERY_DISPLAY = {
  started: { label: 'Started', tone: 'neutral' },
  paused: { label: 'Paused', tone: 'neutral' },
  unconfirmed: { label: 'Not confirmed', tone: 'warn' },
} as const;

/** The status as the person should read it: a stalled publish has stopped. */
export function statusDisplay(record: MetaAdDraftRecord, now: number): { readonly label: string; readonly tone: StatusTone } {
  if (record.status === 'published') {
    if (record.delivery === 'active') return DELIVERY_DISPLAY.started;
    if (record.delivery === 'unconfirmed') return DELIVERY_DISPLAY.unconfirmed;
    if (record.startedAt !== null) return DELIVERY_DISPLAY.paused;
  }
  return publishStalled(record, now) ? STATUS_DISPLAY.failed : STATUS_DISPLAY[record.status];
}

/** The status badge, shared by the draft page and the list. */
export function DraftStatusBadge({ record, now }: { record: MetaAdDraftRecord; now: number }) {
  const status = statusDisplay(record, now);
  const live = record.status === 'publishing' && !publishStalled(record, now);
  return (
    <span className="inline-flex items-center gap-2">
      {/* Slice 66: the Blueprint status dot, except where a play or pause glyph
          already gives the pill its shape — two marks before one word is noise.
          The dot is aria-hidden and in the badge's own colour; the word is the
          status. */}
      <Badge
        tone={status.tone}
        dot={!(record.status === 'published' && (record.delivery === 'active' || record.delivery === 'paused'))}
        data-role="meta-ad-draft-status"
      >
        {record.status === 'published' && record.delivery === 'active' && (
          <Play aria-hidden="true" className="mr-1 size-3" />
        )}
        {record.status === 'published' && record.delivery === 'paused' && (
          <Pause aria-hidden="true" className="mr-1 size-3" />
        )}
        {status.label}
      </Badge>
      {live && <Spinner size="sm" label={null} />}
    </span>
  );
}

/**
 * `review` is the page a person reads before Publish: the status, and the
 * budget drawn large. `record` is the same draft folded under a published
 * campaign's controls: what was composed, as the record of it — no status of
 * its own, because Meta's leads the page, and no budget drawn large, because
 * the one drawn large on that page is Meta's.
 */
export function MetaAdDraftSummary({
  record,
  now = Date.now(),
  variant = 'review',
}: {
  record: MetaAdDraftRecord;
  now?: number;
  variant?: 'review' | 'record';
}) {
  const { draft, published } = record;
  const { adSet } = draft;
  const money = formatMetaMoney(adSet.budget.amount, record.currency);
  const { targeting } = adSet;

  return (
    <div data-role="meta-ad-draft-summary" data-version={record.version} className="flex flex-col gap-6">
      {variant === 'review' && (
        <div className="flex flex-wrap items-center gap-2">
          <DraftStatusBadge record={record} now={now} />
        </div>
      )}

      {/*
        THE FAILURE IS THE ONE SENTENCE THIS SUMMARY PRINTS. A publish that
        stopped says why, and what it created before stopping is listed below —
        publishing again resumes from there. Slice 52: a correction saved
        against that draft clears `lastError` at the API, so this prints
        nothing afterwards — a red "the budget is below Meta's minimum" above a
        budget just raised describes a version that no longer exists.
      */}
      {record.status === 'failed' && record.lastError !== null && (
        <p data-role="meta-ad-draft-error" className="max-w-prose text-[0.88rem] leading-relaxed text-bad">
          {record.lastError}
        </p>
      )}

      <dl className="grid gap-x-8 gap-y-4 sm:grid-cols-2 lg:grid-cols-4">
        {/* `META_BUDGET_LABELS` and not words of its own: this summary and the
            delivery figures are drawn on one page, and "Budget in total" above
            one figure with "Budget for the whole run" above another said the
            same amount two ways. The money screens' table is the one table. */}
        <Fact label={META_BUDGET_LABELS.ad_set[adSet.budget.kind]}>
          <span
            data-role="meta-ad-draft-budget"
            className={
              variant === 'review'
                ? 'font-mono text-[1.25rem] leading-none tabular-nums'
                : 'font-mono text-[0.88rem] tabular-nums'
            }
          >
            {money ?? <Badge tone="neutral">{record.currency}</Badge>}
          </span>
        </Fact>
        <Fact label="Ends">
          {adSet.endsOn === null ? <Badge tone="neutral">No end date</Badge> : <Mono>{adSet.endsOn}</Mono>}
        </Fact>
        <Fact label="Objective">{OBJECTIVE_LABEL[draft.campaign.objective]}</Fact>
        <Fact label="Destination">{DESTINATION_LABEL[metaDraftDestination(draft)]}</Fact>
        <Fact label="Special ad category">{CATEGORY_LABEL[draft.campaign.specialAdCategory]}</Fact>
        <Fact label="Ad set">{adSet.name}</Fact>
        <Fact label="Page">
          <Mono>{draft.pageId}</Mono>
        </Fact>
        <Fact label="Instagram account">
          {draft.instagramAccountId === null ? <Badge tone="neutral">None</Badge> : <Mono>{draft.instagramAccountId}</Mono>}
        </Fact>
        <Fact label="Countries">
          <Mono>{targeting.countries.join(', ')}</Mono>
        </Fact>
        <Fact label="Ages">
          <Mono>
            {targeting.ageMin}–{targeting.ageMax}
            {targeting.ageMax === 65 ? '+' : ''}
          </Mono>
        </Fact>
        <Fact label="Gender">{targeting.gender === 'all' ? 'Everyone' : targeting.gender === 'male' ? 'Men' : 'Women'}</Fact>
        <Fact label="Placements">
          {targeting.placements === null ? (
            /* Automatic is everywhere — the same null the read decodes. */
            <Badge tone="neutral">Automatic</Badge>
          ) : (
            targeting.placements.map((one) => PLACEMENT_LABEL[one]).join(', ')
          )}
        </Fact>
      </dl>

      <ul data-role="meta-ad-draft-ads" className="flex flex-col gap-3">
        {draft.ads.map((ad, index) => {
          const adId = published.adIds[index] ?? null;
          const { creative } = ad;
          return (
            <li
              key={`${String(index)}-${ad.name}`}
              data-role="meta-ad-draft-ad"
              className="flex flex-col gap-3 rounded-base border border-line bg-white/[0.02] p-4"
            >
              <div className="flex flex-wrap items-center gap-2">
                <Badge tone="neutral">{FORMAT_LABEL[creative.format]}</Badge>
                <span className="text-[0.92rem] text-text">{ad.name}</span>
                {adId !== null && <span className="font-mono text-[0.72rem] text-muted">Ad ID {adId}</span>}
              </div>
              <dl className="grid gap-x-8 gap-y-3 sm:grid-cols-2">
                <Fact label="Primary text">
                  <span className="whitespace-pre-wrap">{creative.primaryText}</span>
                </Fact>
                <Fact label="Button">{buttonLabel(creative)}</Fact>
                {creative.format !== 'carousel' && (
                  <>
                    <Fact label="Headline">{creative.headline}</Fact>
                    <Fact label="Description">
                      {creative.description === null ? <Badge tone="neutral">None</Badge> : creative.description}
                    </Fact>
                    {/* A chat ad has no link of its own — it opens WhatsApp, which the destination says. */}
                    {(creative.format === 'single_image' || creative.format === 'single_video') && (
                      <Fact label="Link">
                        <Address>{creative.link}</Address>
                      </Fact>
                    )}
                    {creative.format === 'single_image' || creative.format === 'chat_image' ? (
                      <Fact label="Image">
                        <Address>{creative.imageUrl}</Address>
                      </Fact>
                    ) : (
                      <>
                        <Fact label="Video">
                          <Address>{creative.videoUrl}</Address>
                        </Fact>
                        <Fact label="Thumbnail">
                          <Address>{creative.thumbnailUrl}</Address>
                        </Fact>
                      </>
                    )}
                  </>
                )}
              </dl>
              {creative.format === 'carousel' && (
                <ol className="flex flex-col gap-2" data-role="meta-ad-draft-cards">
                  {creative.cards.map((card, at) => (
                    <li key={at} className="flex flex-col gap-2 rounded-base border border-line p-3">
                      <Badge tone="neutral" className="self-start">
                        Card {at + 1}
                      </Badge>
                      <dl className="grid gap-x-8 gap-y-2 sm:grid-cols-2">
                        <Fact label="Headline">{card.headline}</Fact>
                        <Fact label="Description">
                          {card.description === null ? <Badge tone="neutral">None</Badge> : card.description}
                        </Fact>
                        <Fact label="Link">
                          <Address>{card.link}</Address>
                        </Fact>
                        <Fact label="Image">
                          <Address>{card.imageUrl}</Address>
                        </Fact>
                      </dl>
                    </li>
                  ))}
                </ol>
              )}
            </li>
          );
        })}
      </ul>

      {published.campaignId !== null && (
        <dl data-role="meta-ad-draft-ids" className="grid gap-x-8 gap-y-3 sm:grid-cols-2 lg:grid-cols-4">
          <Fact label="Ad account">
            {/* A campaign id is never on file without its account — see
                `metaPublishedIdsSchema`. */}
            <Mono>{published.adAccountId}</Mono>
          </Fact>
          <Fact label="Campaign at Meta">
            <Mono>{published.campaignId}</Mono>
          </Fact>
          <Fact label="Ad set at Meta">
            {published.adSetId === null ? <Badge tone="neutral">Not created</Badge> : <Mono>{published.adSetId}</Mono>}
          </Fact>
          <Fact label="Ads at Meta">
            <Mono>
              {published.adIds.filter((one) => one !== null).length} / {draft.ads.length}
            </Mono>
          </Fact>
          {record.publishedAt !== null && (
            <Fact label="Published">
              <Mono>{formatDate(record.publishedAt)}</Mono>
            </Fact>
          )}
        </dl>
      )}
    </div>
  );
}

/** A labelled fact — shared with the publish confirmation, so both read alike. */
export function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <Kicker as="dt">{label}</Kicker>
      <dd className="mt-1 break-words text-[0.92rem] text-text">{children}</dd>
    </div>
  );
}

export function Mono({ children }: { children: ReactNode }) {
  return <span className="font-mono text-[0.88rem] tabular-nums">{children}</span>;
}

/**
 * A link or media address, whole and unlinked: the reviewer reads exactly what
 * Meta will be sent, and a stray click on the review page opens nothing.
 */
function Address({ children }: { children: ReactNode }) {
  return <span className="break-all font-mono text-[0.82rem]">{children}</span>;
}

/**
 * Whether no live publish holds this draft. A stalled publish counts as
 * stopped — the API accepts an edit of one, on the same threshold.
 */
function isOpen(record: MetaAdDraftRecord, now: number): boolean {
  return record.status === 'draft' || record.status === 'failed' || publishStalled(record, now);
}

/**
 * Whether a draft can still be edited whole, or discarded: nothing of it
 * exists at Meta, and no live publish holds it.
 *
 * Discard keeps this gate and only this gate, and the reason is slice 42's
 * fourth rule: once a campaign exists at Meta this row is the only record of
 * it, so deleting the row would leave a campaign nobody in Wend can name.
 * {@link isCorrectable} is the other half — a draft with a campaign at Meta is
 * not editable and not discardable, and is correctable.
 */
export function isEditable(record: MetaAdDraftRecord, now: number): boolean {
  return isOpen(record, now) && record.published.campaignId === null;
}

/**
 * Slice 52, gap 1: whether a draft a publish stopped after creating its
 * campaign can be corrected — the parts not yet at Meta changed, the parts at
 * Meta frozen, and the publish resumed into the ids already recorded.
 *
 * It is deliberately not folded into {@link isEditable}. On the draft page
 * "Edit" has always meant the whole draft with nothing at Meta; a second
 * meaning for one word on one page is the finding slice 44 recorded about
 * "draft", and the correction is offered under its own name instead. What the
 * composer may change once it is open is `frozenAtMeta`'s, not this.
 */
export function isCorrectable(record: MetaAdDraftRecord, now: number): boolean {
  return isOpen(record, now) && record.published.campaignId !== null;
}

/** Whether Publish is offered. A stopped or stalled draft resumes; a published one is done. */
export function isPublishable(record: MetaAdDraftRecord, now: number): boolean {
  return record.status === 'draft' || record.status === 'failed' || publishStalled(record, now);
}
